/**
 * Offline Grace Period Manager
 * Handles offline validation periods when license can't be validated online
 */

import type { OfflineGracePeriodStatus } from '$lib/types/subscription';
import type { OfflineGracePeriodManager } from './interfaces';
import { LICENSE_CONFIG } from '$lib/constants/subscription';

/**
 * Grace period storage entry
 */
interface GracePeriodEntry {
  licenseKey: string;
  startedAt: number;
  lastValidationAt: number;
  gracePeriodHours: number;
  extensionHours: number;
  status: 'active' | 'expired' | 'ended';
  reason: 'network_error' | 'server_unavailable' | 'manual' | 'rate_limited';
  attemptCount: number;
  lastAttemptAt: number;
}

/**
 * Grace period cleanup record
 */
interface CleanupRecord {
  lastCleanup: number;
  entriesRemoved: number;
}

export class OfflineGracePeriodManagerImpl implements OfflineGracePeriodManager {
  private readonly STORAGE_PREFIX = 'grace_period_';
  private readonly CLEANUP_RECORD_KEY = 'grace_period_cleanup';
  private readonly DEFAULT_GRACE_HOURS = LICENSE_CONFIG.OFFLINE_GRACE_PERIOD_DAYS * 24;
  private readonly CLEANUP_INTERVAL_MS = 24 * 60 * 60 * 1000; // 24 hours
  
  // Cache for grace period status
  private statusCache = new Map<string, { status: OfflineGracePeriodStatus; expiresAt: number }>();

  constructor() {
    this.initializeManager();
  }

  /**
   * Initialize the manager
   */
  private initializeManager(): void {
    // Schedule periodic cleanup
    this.schedulePeriodicCleanup();
  }

  /**
   * Start offline grace period
   */
  async startGracePeriod(licenseKey: string): Promise<void> {
    try {
      const now = Date.now();
      const existing = await this.getGracePeriodEntry(licenseKey);
      
      if (existing && existing.status === 'active') {
        // Update existing grace period
        existing.lastAttemptAt = now;
        existing.attemptCount += 1;
      } else {
        // Create new grace period
        const entry: GracePeriodEntry = {
          licenseKey,
          startedAt: now,
          lastValidationAt: now,
          gracePeriodHours: this.DEFAULT_GRACE_HOURS,
          extensionHours: 0,
          status: 'active',
          reason: 'network_error',
          attemptCount: 1,
          lastAttemptAt: now
        };
        
        await this.saveGracePeriodEntry(licenseKey, entry);
      }

      // Clear cache
      this.statusCache.delete(licenseKey);
    } catch (error) {
      console.error(`Failed to start grace period for ${licenseKey}:`, error);
    }
  }

  /**
   * Check if in grace period
   */
  async isInGracePeriod(licenseKey: string): Promise<boolean> {
    try {
      const status = await this.getGracePeriodStatus(licenseKey);
      return status.isInGracePeriod;
    } catch (error) {
      console.error(`Failed to check grace period for ${licenseKey}:`, error);
      return false;
    }
  }

  /**
   * Get grace period status
   */
  async getGracePeriodStatus(licenseKey: string): Promise<OfflineGracePeriodStatus> {
    // Check cache first
    const cached = this.statusCache.get(licenseKey);
    if (cached && Date.now() < cached.expiresAt) {
      return cached.status;
    }

    try {
      const entry = await this.getGracePeriodEntry(licenseKey);
      
      if (!entry) {
        // No grace period exists
        const status: OfflineGracePeriodStatus = {
          isInGracePeriod: false,
          lastValidationAt: new Date(0), // Unix epoch
          gracePeriodExpiresAt: new Date(0),
          daysRemaining: 0
        };
        
        this.cacheStatus(licenseKey, status);
        return status;
      }

      const now = Date.now();
      const totalGraceMs = (entry.gracePeriodHours + entry.extensionHours) * 60 * 60 * 1000;
      const graceExpiresAt = entry.startedAt + totalGraceMs;
      const isInGracePeriod = now < graceExpiresAt && entry.status === 'active';
      
      const msRemaining = Math.max(0, graceExpiresAt - now);
      const daysRemaining = Math.ceil(msRemaining / (24 * 60 * 60 * 1000));

      const status: OfflineGracePeriodStatus = {
        isInGracePeriod,
        lastValidationAt: new Date(entry.lastValidationAt),
        gracePeriodExpiresAt: new Date(graceExpiresAt),
        daysRemaining
      };

      // Update entry status if expired
      if (!isInGracePeriod && entry.status === 'active') {
        entry.status = 'expired';
        await this.saveGracePeriodEntry(licenseKey, entry);
      }

      this.cacheStatus(licenseKey, status);
      return status;
    } catch (error) {
      console.error(`Failed to get grace period status for ${licenseKey}:`, error);
      
      // Return safe default
      return {
        isInGracePeriod: false,
        lastValidationAt: new Date(0),
        gracePeriodExpiresAt: new Date(0),
        daysRemaining: 0
      };
    }
  }

  /**
   * End grace period (successful online validation)
   */
  async endGracePeriod(licenseKey: string): Promise<void> {
    try {
      const entry = await this.getGracePeriodEntry(licenseKey);
      
      if (entry && entry.status === 'active') {
        entry.status = 'ended';
        entry.lastValidationAt = Date.now();
        await this.saveGracePeriodEntry(licenseKey, entry);
        
        // Clear cache
        this.statusCache.delete(licenseKey);
      }
    } catch (error) {
      console.error(`Failed to end grace period for ${licenseKey}:`, error);
    }
  }

  /**
   * Extend grace period
   */
  async extendGracePeriod(licenseKey: string, additionalHours: number): Promise<void> {
    try {
      const entry = await this.getGracePeriodEntry(licenseKey);
      
      if (entry) {
        entry.extensionHours += additionalHours;
        entry.status = 'active'; // Reactivate if was expired
        await this.saveGracePeriodEntry(licenseKey, entry);
        
        // Clear cache
        this.statusCache.delete(licenseKey);
      }
    } catch (error) {
      console.error(`Failed to extend grace period for ${licenseKey}:`, error);
    }
  }

  /**
   * Clean expired grace periods
   */
  async cleanExpiredGracePeriods(): Promise<void> {
    try {
      const entries = await this.getAllGracePeriodEntries();
      let removedCount = 0;
      const now = Date.now();
      
      for (const [licenseKey, entry] of entries) {
        const totalGraceMs = (entry.gracePeriodHours + entry.extensionHours) * 60 * 60 * 1000;
        const graceExpiresAt = entry.startedAt + totalGraceMs;
        const daysSinceExpiration = (now - graceExpiresAt) / (24 * 60 * 60 * 1000);
        
        // Remove entries that have been expired for more than 30 days
        if (daysSinceExpiration > 30) {
          await this.removeGracePeriodEntry(licenseKey);
          removedCount++;
        }
      }

      // Update cleanup record
      const cleanupRecord: CleanupRecord = {
        lastCleanup: now,
        entriesRemoved: removedCount
      };
      
      localStorage.setItem(this.CLEANUP_RECORD_KEY, JSON.stringify(cleanupRecord));
      
      if (removedCount > 0) {
      }
    } catch (error) {
      console.error('Failed to clean expired grace periods:', error);
    }
  }

  /**
   * Private helper methods
   */

  private getStorageKey(licenseKey: string): string {
    return `${this.STORAGE_PREFIX}${licenseKey}`;
  }

  private async getGracePeriodEntry(licenseKey: string): Promise<GracePeriodEntry | null> {
    try {
      const storageKey = this.getStorageKey(licenseKey);
      const stored = localStorage.getItem(storageKey);
      
      if (!stored) {
        return null;
      }

      return JSON.parse(stored) as GracePeriodEntry;
    } catch (error) {
      console.error(`Failed to get grace period entry for ${licenseKey}:`, error);
      return null;
    }
  }

  private async saveGracePeriodEntry(licenseKey: string, entry: GracePeriodEntry): Promise<void> {
    try {
      const storageKey = this.getStorageKey(licenseKey);
      localStorage.setItem(storageKey, JSON.stringify(entry));
    } catch (error) {
      console.error(`Failed to save grace period entry for ${licenseKey}:`, error);
    }
  }

  private async removeGracePeriodEntry(licenseKey: string): Promise<void> {
    try {
      const storageKey = this.getStorageKey(licenseKey);
      localStorage.removeItem(storageKey);
      this.statusCache.delete(licenseKey);
    } catch (error) {
      console.error(`Failed to remove grace period entry for ${licenseKey}:`, error);
    }
  }

  private async getAllGracePeriodEntries(): Promise<Map<string, GracePeriodEntry>> {
    const entries = new Map<string, GracePeriodEntry>();
    
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(this.STORAGE_PREFIX)) {
        const licenseKey = key.substring(this.STORAGE_PREFIX.length);
        const entry = await this.getGracePeriodEntry(licenseKey);
        
        if (entry) {
          entries.set(licenseKey, entry);
        }
      }
    }
    
    return entries;
  }

  private cacheStatus(licenseKey: string, status: OfflineGracePeriodStatus): void {
    // Cache status for 5 minutes
    const expiresAt = Date.now() + 5 * 60 * 1000;
    this.statusCache.set(licenseKey, { status, expiresAt });
  }

  private schedulePeriodicCleanup(): void {
    // Check if cleanup is needed
    const shouldCleanup = this.shouldRunCleanup();
    
    if (shouldCleanup) {
      // Run cleanup in next tick to avoid blocking initialization
      setTimeout(() => {
        this.cleanExpiredGracePeriods().catch(error => {
          console.error('Periodic cleanup failed:', error);
        });
      }, 0);
    }

    // Schedule next cleanup check
    setTimeout(() => {
      this.schedulePeriodicCleanup();
    }, this.CLEANUP_INTERVAL_MS);
  }

  private shouldRunCleanup(): boolean {
    try {
      const cleanupRecord = localStorage.getItem(this.CLEANUP_RECORD_KEY);
      if (!cleanupRecord) {
        return true; // Never run cleanup before
      }

      const record: CleanupRecord = JSON.parse(cleanupRecord);
      const timeSinceLastCleanup = Date.now() - record.lastCleanup;
      
      // Run cleanup every 24 hours
      return timeSinceLastCleanup > this.CLEANUP_INTERVAL_MS;
    } catch (error) {
      console.error('Failed to check cleanup schedule:', error);
      return true; // Run cleanup on error
    }
  }

  /**
   * Get grace period statistics (for debugging/monitoring)
   */
  async getGracePeriodStatistics(): Promise<{
    totalEntries: number;
    activeEntries: number;
    expiredEntries: number;
    endedEntries: number;
  }> {
    try {
      const entries = await this.getAllGracePeriodEntries();
      
      let active = 0, expired = 0, ended = 0;
      
      for (const entry of entries.values()) {
        switch (entry.status) {
          case 'active': active++; break;
          case 'expired': expired++; break;
          case 'ended': ended++; break;
        }
      }

      return {
        totalEntries: entries.size,
        activeEntries: active,
        expiredEntries: expired,
        endedEntries: ended
      };
    } catch (error) {
      console.error('Failed to get grace period statistics:', error);
      return {
        totalEntries: 0,
        activeEntries: 0,
        expiredEntries: 0,
        endedEntries: 0
      };
    }
  }
}